<template>
  <b-modal
    :active="isModalActive"
    has-modal-card
    trap-focus
    aria-modal
    :width="600"
    :can-cancel="canCancel"
    @close="handleCloseModal({ hasUserUpgraded: false })"
  >
    <div class="card">
      <header class="card-header">
        <p class="card-header-title is-inline has-text-centered">
          Looks like your team is growing!
        </p>
      </header>
      <div class="card-content u-p-y-0 has-text-centered">
        <span class="title is-1" role="img">🙌</span>
      </div>
      <div class="card-content u-p-y-0 has-text-centered">
        You've reached the maximum number of users in our Free plan.
      </div>
      <div v-if="isLoggedInUserWorkspaceOwner" class="card-content u-p-t-small">
        <p class="u-m-b-small">
          Upgrade to our <span class="has-text-weight-bold">Growth</span> plan
          to expand your workspace.
        </p>
        <p class="subtitle is-size-6">
          {{ subscriptionDetails.description }}
        </p>
        <div class="content">
          <ul>
            <li
              v-for="planDetail in subscriptionDetails.planDetails"
              :key="planDetail.key"
            >
              {{ planDetail }}
            </li>
          </ul>
        </div>
        <p>
          With a total of
          <span class="has-text-weight-bold">{{ workspaceUserCount }} users</span>
          you'll be billed
          <span class="has-text-weight-bold">${{ subscriptionDetails.pricePerUser * workspaceUserCount }}</span>
          per month
        </p>
        <p class="label u-m-t">Credit Card Details</p>
        <card
          class="u-m-b"
          :stripe="stripeKey"
          @change="handleCardFieldChange"
          ref="stripeCardComponent"
        />
        <b-button
          type="is-primary"
          class="pay-with-stripe"
          @click="handleCompletePayment"
          @keyup.enter="handleCompletePayment"
          :disabled='!isCardFieldComplete'
          :loading="isPaymentInProgress"
          expanded
        >
          Pay with credit card
        </b-button>
        <b-message
          class="u-m-t"
          v-if="paymentError"
          role="alert"
          type="is-danger"
        >
          {{ paymentError }}
        </b-message>
      </div>
      <div v-else class="card-content u-p-t-small has-text-centered">
        <p class="u-m-b-small">
          Ask the owner of your workspace to upgrade to our <span class="has-text-weight-bold">Growth</span> plan
          to continue using StandupPro
        </p>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { Card, confirmCardSetup } from '@juanfabrega/vue-stripe-elements-plus';
import * as SubscriptionTypes from '@/constants/Subscription.types';
import * as NotificationTypes from '@/constants/notificationTypes';
import * as ApiService from '@/services/Api.service';
import * as NotificationService from '@/services/Notification.service';
import * as SubscriptionActions from '@/store/actions/Subscription.actions';

export default {
  name: 'UpgradeSubscriptionModal',

  components: {
    Card,
  },

  props: {
    isModalActive: {
      type: Boolean,
      required: false,
      default: false,
    },
    workspaceUserCount: {
      type: Number,
      required: true,
    },
    canCancel: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  data() {
    return {
      isCardFieldComplete: false,
      paymentError: null,
      isPaymentInProgress: false,
    };
  },

  computed: {
    ...mapGetters([
      'activeWorkspace',
      'activeWorkspaceSubscription',
      'isLoggedInUserWorkspaceOwner',
    ]),
    stripeKey() {
      if (this.$env === 'prod') {
        return 'pk_live_uCXguGcZ4R9gM4nsq8lzoVtz00msA8aFm3';
      }

      return 'pk_test_7C6E11IxIgDRaTLokl9DcSAf00AwZHqRwG';
    },
    subscriptionDetails() {
      return {
        name: 'Growth',
        description: 'For growing businesses with 6 or more users.',
        planDetails: [
          'All StandupPro features',
          '$5 per user per month',
        ],
        pricePerUser: 5,
        maxUsers: -1,
      };
    },
  },

  methods: {
    handleCloseModal({ hasUserUpgraded }) {
      this.$emit('close', { hasUserUpgraded });
    },
    async handleCompletePayment() {
      this.isPaymentInProgress = true;
      this.paymentError = null;

      try {
        // Create a Stripe Setup Intent
        const { data: { setupIntent } } = await ApiService.createStripeSetupIntent({
          customerId: this.activeWorkspaceSubscription.stripeCustomerId,
        });

        // Bit of a hack to get the stripe card element. @TODO -- cleanup if possible.
        const cardElement = this.$refs.stripeCardComponent.$refs.element._element; // eslint-disable-line
        const cardSetupConfirmation = await confirmCardSetup(setupIntent.client_secret, { payment_method: { card: cardElement } });

        // Payment method needs to be added as default in order to work for a subscription setup.
        await ApiService.updateStripeSubscription({
          subscriptionId: this.activeWorkspaceSubscription.stripeSubscriptionId,
          updatedSubscription: { default_payment_method: cardSetupConfirmation.setupIntent.payment_method },
        });

        const { data: { subscription: stripeSubscription } } = await ApiService.getStripeSubscription(
          this.activeWorkspaceSubscription.stripeSubscriptionId,
        );

        // Update customer subscription items in Stripe
        await ApiService.updateStripeSubscriptionItemById({
          subscriptionItemId: stripeSubscription.items.data[0].id,
          newPriceId: SubscriptionTypes.STRIPE_PRICE_TYPE_GROWTH(this.$env),
          quantity: this.activeWorkspace.userCount,
        });

        // Update customer subscription in firestore DB
        await this.$store.dispatch(
          SubscriptionActions.UPDATE_SUBSCRIPTION_BY_ID,
          {
            subscriptionId: this.activeWorkspaceSubscription.id,
            updatedSubscription: { stripePricingPlan: SubscriptionTypes.STRIPE_PRICE_TYPE_GROWTH(this.$env) },
          },
        );

        NotificationService.showNotification(NotificationTypes.UPGRADE_SUBSCRIPTION_SUCCESS);
        this.handleCloseModal({ hasUserUpgraded: true });
      } catch (err) {
        this.paymentError = err;
      }

      this.isPaymentInProgress = false;
    },
    handleCardFieldChange(event) {
      this.isCardFieldComplete = event.complete;
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
